import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { IInfiniteGetNodeContentQueryData } from '@/api/nodes/queries/get-node-content-query'
import { updateInfiniteNodesOptimistically } from '@/api/nodes/helpers/update-infinite-nodes-optimistically'
import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { QueryKey } from '@tanstack/react-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import {
    IOrganizationDeleteNodeRequestBody,
    IOrganizationDeleteNodeResponseData,
} from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/nodes/node/delete'

interface IDeleteNodesMutationParams {
    organizationId: string
    brandSlug: string
}

export class DeleteNodesMutation {
    static call(
        api: IApi,
        { organizationId, brandSlug, ...payload }: IDeleteNodesMutationParams & IOrganizationDeleteNodeRequestBody
    ) {
        return api.post<IOrganizationDeleteNodeResponseData, IOrganizationDeleteNodeRequestBody>(
            `/organization/${organizationId}/brands/${brandSlug}/nodes/node/delete`,
            payload
        )
    }
}

interface IUseDeleteNodesMutationParams {
    nodeContentQueryKey: QueryKey
    sendToastSuccess: (count: number) => unknown
    sendToastError: (count: number) => unknown
    invalidateOnSuccess?: boolean
}

export const useDeleteNodesMutation = ({
    nodeContentQueryKey,
    sendToastError,
    sendToastSuccess,
}: IUseDeleteNodesMutationParams) => {
    const { api, apiParams, qc } = useApiToolbox()

    return useApiMutation({
        mutationFn: ({ paths: rawPaths, ...payload }: IOrganizationDeleteNodeRequestBody) => {
            const paths = Array.from(new Set(rawPaths))

            const promise = DeleteNodesMutation.call(api, {
                ...payload,
                brandSlug: apiParams.brandSlug,
                paths,
                organizationId: apiParams.organizationId,
            })

            let prev: IInfiniteGetNodeContentQueryData | undefined

            updateInfiniteNodesOptimistically({
                qc,
                count: paths.length,
                nodeContentQueryKey,
                onSuccess: sendToastSuccess,

                filterPageData: (node) => !paths.includes(node.path),

                assignPrev: (prevData) => {
                    prev = prevData
                },
            })

            return promise
                .then((result) => {
                    // Invalidation promise must be returned
                    return qc
                        .invalidateQueries({
                            predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                        })
                        .then(() => result)
                        .catch(() => result)
                })
                .catch((e) => {
                    sendToastError(paths.length)

                    qc.setQueryData<IInfiniteGetNodeContentQueryData>(nodeContentQueryKey, prev)
                    qc.refetchQueries({
                        predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                    })

                    throw e
                })
        },
    })
}
